import { useEffect, useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, StatCard, SearchBar, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, Pencil, X, Banknote, Coins, AlertCircle, Wallet, Truck, Users, Building2, RotateCcw, Check, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PAY = {
  paye: { label: "Payée", bg: "rgba(76,175,80,.15)", c: "#4CAF50" },
  partiel: { label: "Partiel", bg: "rgba(255,152,0,.15)", c: "#FF9800" },
  impaye: { label: "Impayée", bg: "rgba(244,67,54,.15)", c: "#F44336" },
  retour: { label: "Retour", bg: "rgba(33,150,243,.15)", c: "#2196F3" },
  cloture: { label: "Clôturée", bg: "rgba(160,160,160,.18)", c: "#9E9E9E" },
};
const CLIENT_TYPES = [["snack", "Snack partenaire"], ["marche", "Marché"], ["commerce", "Commerce"], ["professionnel", "Client professionnel"]];

function PayBadge({ s }) {
  const x = PAY[s] || PAY.impaye;
  return <span className="tm-badge" style={{ background: x.bg, color: x.c }}>{x.label}</span>;
}

export default function Distribution() {
  const { isAdmin, can } = useAuth();
  const [tab, setTab] = useState("livraisons");
  const [query, setQuery] = useState("");
  const { data: clients, refetch: refetchClients } = useData("/distribution/clients");
  const { data: livraisons, loading, refetch: refetchLiv } = useData("/distribution/livraisons");
  const { data: retours, refetch: refetchRetours } = useData("/distribution/retours");
  const { data: products } = useData("/products");
  const [summary, setSummary] = useState(null);

  const loadSummary = () => api.get("/distribution/summary").then(({ data }) => setSummary(data)).catch(() => {});
  useEffect(() => { loadSummary(); }, [livraisons, retours]);

  // Client modal
  const [cOpen, setCOpen] = useState(false);
  const EMPTY_CLIENT = { nom: "", type: "snack", contact: "", telephone: "", adresse: "", ville: "", email: "", conditions_paiement: "", note: "", observations: "" };
  const [cForm, setCForm] = useState(EMPTY_CLIENT);
  const [cEdit, setCEdit] = useState(null);
  const openClient = (c) => { c ? (setCForm({ ...EMPTY_CLIENT, ...c }), setCEdit(c.id)) : (setCForm(EMPTY_CLIENT), setCEdit(null)); setCOpen(true); };
  const saveClient = async (e) => {
    e.preventDefault();
    try {
      if (cEdit) await api.put(`/distribution/clients/${cEdit}`, cForm);
      else await api.post("/distribution/clients", cForm);
      toast.success("Client enregistré"); setCOpen(false); refetchClients();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const removeClient = async (c) => { if (!window.confirm(`Supprimer ${c.nom} ?`)) return; await api.delete(`/distribution/clients/${c.id}`); toast.success("Supprimé"); refetchClients(); };

  // Livraison modal
  const [lOpen, setLOpen] = useState(false);
  const [lClient, setLClient] = useState("");
  const [lFacture, setLFacture] = useState("");
  const [lPaye, setLPaye] = useState("");
  const [cart, setCart] = useState([]);
  const [selProd, setSelProd] = useState("");
  const [qte, setQte] = useState(1);
  const [prix, setPrix] = useState("");
  const [cout, setCout] = useState("");

  const openLiv = () => {
    if (clients.length === 0) return toast.error("Ajoutez d'abord un client professionnel");
    const p0 = products[0];
    setLClient(clients[0]?.id || ""); setLFacture(""); setLPaye(""); setCart([]); setSelProd(p0?.id || ""); setQte(1);
    setPrix(p0?.prix_vente ?? ""); setCout(p0?.cout_matiere ?? ""); setLOpen(true);
  };
  const onSelProd = (id) => {
    setSelProd(id);
    const p = products.find((x) => x.id === id);
    if (p) { setPrix(p.prix_vente); setCout(p.cout_matiere); }
  };
  const addItem = () => {
    const p = products.find((x) => x.id === selProd);
    if (!p || qte <= 0 || prix === "") return;
    setCart([...cart, { nom: p.nom, quantite: Number(qte), prix_unitaire: parseFloat(prix), cout_unitaire: parseFloat(cout || 0) }]);
    setQte(1);
  };
  const cartTotal = cart.reduce((a, i) => a + i.quantite * i.prix_unitaire, 0);
  const cartMarge = cart.reduce((a, i) => a + i.quantite * (i.prix_unitaire - i.cout_unitaire), 0);
  const saveLiv = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return toast.error("Ajoutez au moins un produit");
    try {
      await api.post("/distribution/livraisons", { client_id: lClient, items: cart, facture_num: lFacture, montant_paye: parseFloat(lPaye || 0) });
      toast.success("Livraison enregistrée"); setLOpen(false); refetchLiv();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const removeLiv = async (l) => { if (!window.confirm("Supprimer cette livraison ?")) return; await api.delete(`/distribution/livraisons/${l.id}`); toast.success("Supprimée"); refetchLiv(); };

  // Paiement modal
  const [pOpen, setPOpen] = useState(false);
  const [pLiv, setPLiv] = useState(null);
  const [pMontant, setPMontant] = useState("");
  const openPay = (l) => { setPLiv(l); setPMontant(""); setPOpen(true); };
  const savePay = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/distribution/livraisons/${pLiv.id}/paiement`, { montant: parseFloat(pMontant) });
      toast.success("Paiement enregistré"); setPOpen(false); refetchLiv();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  // Retour directement sur une ligne de livraison (appliqué immédiatement)
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const [lrOpen, setLrOpen] = useState(false);
  const [lrLiv, setLrLiv] = useState(null);
  const [lrForm, setLrForm] = useState({ product_nom: "", quantite: 1, montant: "", motif: "invendu", date: "", commentaire: "" });
  const lrItem = () => (lrLiv?.items || []).find((i) => i.nom === lrForm.product_nom);
  const openLineRetour = (l) => {
    const it = (l.items && l.items[0]) || null;
    setLrLiv(l);
    setLrForm({
      product_nom: it ? it.nom : "",
      quantite: 1,
      montant: it ? String(round2(it.prix_unitaire)) : "",
      motif: "invendu",
      date: l.date ? l.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      commentaire: "",
    });
    setLrOpen(true);
  };
  const onLrProd = (nom) => {
    const it = (lrLiv?.items || []).find((i) => i.nom === nom);
    const q = parseFloat(lrForm.quantite) || 0;
    setLrForm({ ...lrForm, product_nom: nom, montant: it ? String(round2(it.prix_unitaire * q)) : lrForm.montant });
  };
  const onLrQte = (v) => {
    const it = lrItem();
    const q = parseFloat(v) || 0;
    setLrForm({ ...lrForm, quantite: v, montant: it ? String(round2(it.prix_unitaire * q)) : lrForm.montant });
  };
  const saveLineRetour = async (e) => {
    e.preventDefault();
    if (!lrForm.product_nom) return toast.error("Sélectionnez le produit retourné");
    const it = lrItem();
    try {
      await api.post("/distribution/retours", {
        client_id: lrLiv.client_id,
        livraison_id: lrLiv.id,
        product_nom: lrForm.product_nom,
        quantite: parseFloat(lrForm.quantite) || 0,
        prix_unitaire: it ? it.prix_unitaire : 0,
        cout_unitaire: it ? (it.cout_unitaire || 0) : 0,
        montant: parseFloat(lrForm.montant) || 0,
        motif: lrForm.motif,
        recuperable: false,
        date: lrForm.date ? new Date(lrForm.date).toISOString() : "",
        commentaire: lrForm.commentaire,
      });
      toast.success("Retour enregistré — montant déduit du solde");
      setLrOpen(false); refetchLiv(); refetchRetours();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  // Retour produit B2B
  const [rOpen, setROpen] = useState(false);
  const [rForm, setRForm] = useState({ client_id: "", product_nom: "", quantite: 1, prix_unitaire: "", cout_unitaire: "", motif: "casse", recuperable: true, commentaire: "" });
  const openRetour = () => {
    if (clients.length === 0) return toast.error("Ajoutez d'abord un client professionnel");
    setRForm({ client_id: clients[0]?.id || "", product_nom: products[0]?.nom || "", quantite: 1, prix_unitaire: products[0]?.prix_vente || "", cout_unitaire: products[0]?.cout_matiere || "", motif: "casse", recuperable: true, commentaire: "" });
    setROpen(true);
  };
  const onRetourProd = (nom) => {
    const p = products.find((x) => x.nom === nom);
    setRForm({ ...rForm, product_nom: nom, prix_unitaire: p?.prix_vente || "", cout_unitaire: p?.cout_matiere || "" });
  };
  const saveRetour = async (e) => {
    e.preventDefault();
    try {
      await api.post("/distribution/retours", { ...rForm, quantite: parseFloat(rForm.quantite), prix_unitaire: parseFloat(rForm.prix_unitaire), cout_unitaire: parseFloat(rForm.cout_unitaire || 0) });
      toast.success("Retour enregistré — en attente de validation admin"); setROpen(false); refetchRetours();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const validerRetour = async (r, decision) => {
    try {
      await api.put(`/distribution/retours/${r.id}/valider?decision=${decision}`);
      toast.success(decision === "valide" ? "Retour validé — CA & bénéfice ajustés" : "Retour refusé");
      refetchRetours();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeRetour = async (r) => { if (!window.confirm("Supprimer ce retour ?")) return; await api.delete(`/distribution/retours/${r.id}`); toast.success("Supprimé"); refetchRetours(); };

  const fmt = (s) => new Date(s).toLocaleDateString("fr-FR");

  const q = query.trim().toLowerCase();
  const match = (fields) => fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  const fLivraisons = q ? livraisons.filter((l) => match([l.client_nom, l.facture_num, PAY[l.statut_paiement]?.label])) : livraisons;
  const fRetours = q ? retours.filter((r) => match([r.client_nom, r.product_nom, r.motif])) : retours;
  const fClients = q ? clients.filter((c) => match([c.nom, c.contact, c.telephone, c.adresse, CLIENT_TYPES.find((t) => t[0] === c.type)?.[1]])) : clients;
  const showSearch = tab !== "stats";
  const searchPlaceholder = tab === "livraisons" ? "Rechercher par client, facture, statut..." : tab === "retours" ? "Rechercher par client, produit, motif..." : "Rechercher un client (nom, contact, tél...)";
  const searchCount = tab === "livraisons" ? fLivraisons.length : tab === "retours" ? fRetours.length : fClients.length;
  const searchTotal = tab === "livraisons" ? livraisons.length : tab === "retours" ? retours.length : clients.length;

  const itemsLabel = (l) => (l.items || []).map((i) => `${i.quantite}× ${i.nom}`).join(", ");
  const qteLivree = (l) => (l.items || []).reduce((a, i) => a + (i.quantite || 0), 0);

  const livCols = [
    { header: "Date", value: (r) => fmt(r.date) },
    { header: "Client", value: (r) => r.client_nom },
    { header: "Produits distribués", value: (r) => itemsLabel(r) },
    { header: "Qté livrée", value: (r) => qteLivree(r) },
    { header: "Montant distribué (DH)", value: (r) => r.total },
    { header: "Encaissé (DH)", value: (r) => r.montant_paye },
    { header: "Retour (DH)", value: (r) => r.montant_retour || 0 },
    { header: "Solde restant (DH)", value: (r) => r.reste_a_payer },
    { header: "Statut", value: (r) => PAY[r.statut_paiement]?.label || r.statut_paiement },
  ];

  return (
    <div>
      <PageHeader title="Distribution B2B" subtitle="Activité professionnelle : snacks, marchés, commerces et clients pro.">
        {tab === "livraisons" && can("distribution", "export") && <ExportButtons onExcel={() => exportExcel("livraisons_b2b", livCols, livraisons)} onPDF={() => exportPDF("Livraisons B2B TIRAMESSI", livCols, livraisons)} />}
        {tab === "clients" && can("distribution", "create") && <button className="tm-btn" onClick={() => openClient(null)} data-testid="add-client-btn"><Plus size={16} /> Nouveau client</button>}
        {tab === "livraisons" && can("distribution", "create") && <button className="tm-btn" onClick={openLiv} data-testid="add-livraison-btn"><Plus size={16} /> Nouvelle livraison</button>}
        {tab === "retours" && can("distribution", "create") && <button className="tm-btn" onClick={openRetour} data-testid="add-retour-btn"><Plus size={16} /> Nouveau retour</button>}
      </PageHeader>

      {summary && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-3">
          <StatCard label="CA brut" value={MAD(summary.ca_total)} icon={Banknote} testid="b2b-ca" />
          <StatCard label="CA net (après retours)" value={MAD(summary.ca_net)} icon={Banknote} accent="#4CAF50" testid="b2b-ca-net" />
          <StatCard label="Retours validés" value={MAD(summary.retours_valeur)} icon={RotateCcw} accent="#F44336" testid="b2b-retours" />
          <StatCard label="Marge nette" value={MAD(summary.marge_net)} icon={Coins} accent="#D4AF37" testid="b2b-marge" />
        </div>
      )}
      {summary && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <StatCard label="Créances clients" value={MAD(summary.creances_total)} icon={AlertCircle} accent="#F44336" testid="b2b-creances" />
          <StatCard label="Encaissé" value={MAD(summary.encaisse_total)} icon={Wallet} accent="#4CAF50" testid="b2b-encaisse" />
          <StatCard label="Livraisons" value={summary.nb_livraisons} icon={Truck} testid="b2b-nb-liv" />
          <StatCard label="Clients pro" value={summary.nb_clients} icon={Users} testid="b2b-nb-clients" />
        </div>
      )}

      <div className="inline-flex p-1 rounded-lg mb-6 flex-wrap" style={{ background: "var(--tm-surface)", border: "1px solid var(--tm-border)" }}>
        {[["livraisons", "Livraisons", Truck], ["retours", "Retours", RotateCcw], ["clients", "Clients pro", Users], ["stats", "Par client", Building2]].map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => { setTab(k); setQuery(""); }} data-testid={`tab-${k}`}
            className="px-4 py-1.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-2"
            style={tab === k ? { background: "#D4AF37", color: "#000" } : { color: "var(--tm-muted)" }}>
            <Icon size={15} /> {lbl}
          </button>
        ))}
      </div>

      {showSearch && <SearchBar value={query} onChange={setQuery} placeholder={searchPlaceholder} testid="distribution-search" count={searchCount} total={searchTotal} />}

      {tab === "livraisons" && (
        loading ? <Loader /> : livraisons.length === 0 ? <Empty text="Aucune livraison enregistrée." /> : fLivraisons.length === 0 ? <Empty text="Aucun résultat pour cette recherche." /> : (
          <div className="tm-card overflow-x-auto" data-testid="livraisons-table">
            <table className="tm-table">
              <thead>
                <tr>
                  <th>Client</th><th>Date</th><th>Produits distribués</th>
                  <th className="text-right">Qté livrée</th>
                  <th className="text-right">Distribué</th><th className="text-right">Encaissé</th>
                  <th className="text-right">Retour</th><th className="text-right">Solde</th>
                  <th>Statut</th><th></th>
                </tr>
              </thead>
              <tbody>
                {fLivraisons.map((l) => (
                  <tr key={l.id} data-testid={`livraison-row-${l.id}`}>
                    <td className="font-semibold">{l.client_nom}</td>
                    <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(l.date)}</td>
                    <td className="text-[var(--tm-muted)] max-w-[220px] truncate" title={itemsLabel(l)}>{itemsLabel(l)}</td>
                    <td className="text-right tabular">{qteLivree(l)}</td>
                    <td className="text-right tabular">{MAD(l.total)}</td>
                    <td className="text-right tabular" style={{ color: "#4CAF50" }} data-testid={`liv-encaisse-${l.id}`}>{MAD(l.montant_paye)}</td>
                    <td className="text-right tabular" style={{ color: (l.montant_retour || 0) > 0 ? "#2196F3" : "var(--tm-muted)" }} data-testid={`liv-retour-${l.id}`}>{MAD(l.montant_retour || 0)}</td>
                    <td className="text-right tabular" style={{ color: l.reste_a_payer > 0 ? "#F44336" : "#4CAF50" }} data-testid={`liv-solde-${l.id}`}>{MAD(l.reste_a_payer)}</td>
                    <td><PayBadge s={l.statut_paiement} /></td>
                    <td className="text-right whitespace-nowrap">
                      {l.reste_a_payer > 0 && can("distribution", "edit") && <button onClick={() => openPay(l)} className="tm-btn-ghost tm-btn text-xs mr-2 py-1" data-testid={`pay-${l.id}`}>Encaisser</button>}
                      {can("distribution", "create") && <button onClick={() => openLineRetour(l)} className="tm-btn-ghost tm-btn text-xs mr-2 py-1" data-testid={`retour-liv-${l.id}`} title="Enregistrer un retour"><RotateCcw size={13} /> Retour</button>}
                      {can("distribution", "delete") && <button onClick={() => removeLiv(l)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={15} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "retours" && (
        retours.length === 0 ? <Empty text="Aucun retour produit enregistré." /> : fRetours.length === 0 ? <Empty text="Aucun résultat pour cette recherche." /> : (
          <div className="tm-card overflow-x-auto" data-testid="retours-table">
            <table className="tm-table">
              <thead>
                <tr><th>Date</th><th>Client</th><th>Produit</th><th className="text-right">Qté</th><th className="text-right">Valeur</th><th>État produit</th><th>Motif</th><th>Validation</th><th></th></tr>
              </thead>
              <tbody>
                {fRetours.map((r) => (
                  <tr key={r.id} data-testid={`retour-row-${r.id}`}>
                    <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(r.date)}</td>
                    <td className="font-semibold">{r.client_nom}</td>
                    <td>{r.product_nom}</td>
                    <td className="text-right tabular">{r.quantite}</td>
                    <td className="text-right tabular" style={{ color: "#F44336" }}>{MAD(r.valeur)}</td>
                    <td>
                      <span className="tm-badge" style={{ background: r.recuperable ? "rgba(76,175,80,.15)" : "rgba(244,67,54,.15)", color: r.recuperable ? "#4CAF50" : "#F44336" }}>
                        {r.recuperable ? "Récupérable" : "Perte"}
                      </span>
                    </td>
                    <td className="text-[var(--tm-muted)]">{r.motif}</td>
                    <td>
                      {r.statut_validation === "valide" ? <span className="tm-badge" style={{ background: "rgba(76,175,80,.15)", color: "#4CAF50" }}><CheckCircle2 size={12} /> Validé</span>
                        : r.statut_validation === "refuse" ? <span className="tm-badge" style={{ background: "rgba(163,163,154,.15)", color: "var(--tm-muted)" }}>Refusé</span>
                        : <span className="tm-badge" style={{ background: "rgba(255,152,0,.15)", color: "#FF9800" }}>En attente</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {can("distribution", "validate") && r.statut_validation === "en_attente" && (
                        <>
                          <button onClick={() => validerRetour(r, "valide")} className="tm-btn-ghost tm-btn text-xs mr-2 py-1" data-testid={`valider-retour-${r.id}`}><Check size={13} /> Valider</button>
                          <button onClick={() => validerRetour(r, "refuse")} className="text-[var(--tm-muted)] hover:text-[#F44336] mr-2"><X size={15} /></button>
                        </>
                      )}
                      {can("distribution", "delete") && <button onClick={() => removeRetour(r)} className="text-[var(--tm-muted)] hover:text-[#F44336]"><Trash2 size={15} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "clients" && (
        clients.length === 0 ? <Empty text="Aucun client professionnel." /> : fClients.length === 0 ? <Empty text="Aucun résultat pour cette recherche." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="clients-grid">
            {fClients.map((c) => (
              <div key={c.id} className="tm-card p-5" data-testid={`client-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-lg">{c.nom}</p>
                    <span className="tm-badge mt-1" style={{ background: "var(--tm-surface-2)", color: "var(--tm-muted)" }}>{CLIENT_TYPES.find((t) => t[0] === c.type)?.[1] || c.type}</span>
                  </div>
                  <div className="flex gap-2">
                    {can("distribution", "edit") && <button onClick={() => openClient(c)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)]" data-testid={`edit-client-${c.id}`} title="Modifier la fiche client"><Pencil size={15} /></button>}
                    {can("distribution", "delete") && <button onClick={() => removeClient(c)} className="text-[var(--tm-muted)] hover:text-[#F44336]" title="Supprimer"><Trash2 size={15} /></button>}
                  </div>
                </div>
                <div className="mt-3 text-sm text-[var(--tm-muted)] space-y-1">
                  {c.contact && <p>Contact : {c.contact}</p>}
                  {c.telephone && <p>Tél : {c.telephone}</p>}
                  {c.email && <p>Email : {c.email}</p>}
                  {(c.adresse || c.ville) && <p>{[c.adresse, c.ville].filter(Boolean).join(", ")}</p>}
                  {c.conditions_paiement && <p>Paiement : {c.conditions_paiement}</p>}
                  {c.note && <p className="italic">{c.note}</p>}
                  {c.observations && <p className="italic">{c.observations}</p>}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "stats" && (
        !summary || summary.clients_stats.length === 0 ? <Empty text="Aucune donnée client." /> : (
          <div className="tm-card overflow-x-auto" data-testid="client-stats-table">
            <table className="tm-table">
              <thead>
                <tr><th>Client</th><th className="text-right">Livraisons</th><th className="text-right">Qté livrée</th><th className="text-right">CA</th><th className="text-right">Marge</th><th className="text-right">Créances</th></tr>
              </thead>
              <tbody>
                {summary.clients_stats.map((c) => (
                  <tr key={c.client}>
                    <td className="font-semibold">{c.client}</td>
                    <td className="text-right tabular">{c.livraisons}</td>
                    <td className="text-right tabular">{c.quantite}</td>
                    <td className="text-right tabular">{MAD(c.ca)}</td>
                    <td className="text-right tabular" style={{ color: "#D4AF37" }}>{MAD(c.marge)}</td>
                    <td className="text-right tabular" style={{ color: c.creances > 0 ? "#F44336" : "#4CAF50" }}>{MAD(c.creances)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Client modal */}
      <Modal open={cOpen} onClose={() => setCOpen(false)} title={cEdit ? "Modifier la fiche client" : "Nouveau client professionnel"} testid="client-modal">
        <form onSubmit={saveClient}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom du client"><input className="tm-input" required value={cForm.nom} onChange={(e) => setCForm({ ...cForm, nom: e.target.value })} data-testid="client-nom" /></Field>
            <Field label="Type">
              <select className="tm-input" value={cForm.type} onChange={(e) => setCForm({ ...cForm, type: e.target.value })} data-testid="client-type">
                {CLIENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Responsable / Contact"><input className="tm-input" value={cForm.contact} onChange={(e) => setCForm({ ...cForm, contact: e.target.value })} data-testid="client-contact" /></Field>
            <Field label="Téléphone"><input className="tm-input" value={cForm.telephone} onChange={(e) => setCForm({ ...cForm, telephone: e.target.value })} data-testid="client-telephone" /></Field>
          </div>
          <Field label="Email"><input type="email" className="tm-input" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} data-testid="client-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Adresse"><input className="tm-input" value={cForm.adresse} onChange={(e) => setCForm({ ...cForm, adresse: e.target.value })} data-testid="client-adresse" /></Field>
            <Field label="Ville"><input className="tm-input" value={cForm.ville} onChange={(e) => setCForm({ ...cForm, ville: e.target.value })} data-testid="client-ville" /></Field>
          </div>
          <Field label="Conditions de paiement"><input className="tm-input" placeholder="Ex : Paiement à 30 jours, comptant..." value={cForm.conditions_paiement} onChange={(e) => setCForm({ ...cForm, conditions_paiement: e.target.value })} data-testid="client-conditions" /></Field>
          <Field label="Notes"><textarea className="tm-input" rows={2} value={cForm.note} onChange={(e) => setCForm({ ...cForm, note: e.target.value })} data-testid="client-note" /></Field>
          <Field label="Observations"><textarea className="tm-input" rows={2} value={cForm.observations} onChange={(e) => setCForm({ ...cForm, observations: e.target.value })} data-testid="client-observations" /></Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="client-save">{cEdit ? "Enregistrer les modifications" : "Créer le client"}</button>
        </form>
      </Modal>

      {/* Livraison modal */}
      <Modal open={lOpen} onClose={() => setLOpen(false)} title="Nouvelle livraison" testid="livraison-modal">
        <form onSubmit={saveLiv}>
          <Field label="Client professionnel">
            <select className="tm-input" value={lClient} onChange={(e) => setLClient(e.target.value)} data-testid="livraison-client">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </Field>
          <div className="flex gap-2 items-end mb-3">
            <div className="flex-1">
              <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Produit</span>
              <select className="tm-input" value={selProd} onChange={(e) => onSelProd(e.target.value)} data-testid="livraison-product">
                {products.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </div>
            <div className="w-16">
              <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Qté</span>
              <input type="number" min="1" className="tm-input" value={qte} onChange={(e) => setQte(e.target.value)} data-testid="livraison-qte" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <Field label="Prix unitaire (DH)"><input type="number" step="0.01" className="tm-input" value={prix} onChange={(e) => setPrix(e.target.value)} data-testid="livraison-prix" /></Field>
            <Field label="Coût unitaire (DH)"><input type="number" step="0.01" className="tm-input" value={cout} onChange={(e) => setCout(e.target.value)} /></Field>
          </div>
          <button type="button" className="tm-btn-ghost tm-btn w-full mb-3" onClick={addItem} data-testid="livraison-add-item"><Plus size={16} /> Ajouter au bon de livraison</button>

          {cart.length > 0 && (
            <div className="tm-card p-3 mb-4" style={{ background: "var(--tm-bg)" }}>
              {cart.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
                  <span>{i.quantite}× {i.nom}</span>
                  <span className="flex items-center gap-3">
                    <span className="tabular">{MAD(i.quantite * i.prix_unitaire)}</span>
                    <button type="button" onClick={() => setCart(cart.filter((_, x) => x !== idx))} className="text-[var(--tm-muted)] hover:text-[#F44336]"><X size={14} /></button>
                  </span>
                </div>
              ))}
              <div className="flex justify-between pt-2 mt-1 font-semibold" style={{ borderTop: "1px solid var(--tm-border)" }}>
                <span>Total / Marge</span><span className="tabular" style={{ color: "var(--tm-gold)" }}>{MAD(cartTotal)} / {MAD(cartMarge)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="N° Facture"><input className="tm-input" value={lFacture} onChange={(e) => setLFacture(e.target.value)} data-testid="livraison-facture" /></Field>
            <Field label="Montant payé (DH)"><input type="number" step="0.01" className="tm-input" value={lPaye} onChange={(e) => setLPaye(e.target.value)} placeholder="0" /></Field>
          </div>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="livraison-save">Enregistrer la livraison</button>
        </form>
      </Modal>

      {/* Retour modal */}
      <Modal open={rOpen} onClose={() => setROpen(false)} title="Nouveau retour produit" testid="retour-modal">
        <form onSubmit={saveRetour}>
          <Field label="Client professionnel">
            <select className="tm-input" value={rForm.client_id} onChange={(e) => setRForm({ ...rForm, client_id: e.target.value })} data-testid="retour-client">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </Field>
          <Field label="Produit retourné">
            <select className="tm-input" value={rForm.product_nom} onChange={(e) => onRetourProd(e.target.value)} data-testid="retour-product">
              {products.map((p) => <option key={p.id} value={p.nom}>{p.nom}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Quantité"><input type="number" step="0.01" min="0" className="tm-input" required value={rForm.quantite} onChange={(e) => setRForm({ ...rForm, quantite: e.target.value })} data-testid="retour-qte" /></Field>
            <Field label="Prix unit. (DH)"><input type="number" step="0.01" className="tm-input" required value={rForm.prix_unitaire} onChange={(e) => setRForm({ ...rForm, prix_unitaire: e.target.value })} /></Field>
            <Field label="Coût unit. (DH)"><input type="number" step="0.01" className="tm-input" value={rForm.cout_unitaire} onChange={(e) => setRForm({ ...rForm, cout_unitaire: e.target.value })} /></Field>
          </div>
          <Field label="Motif du retour">
            <select className="tm-input" value={rForm.motif} onChange={(e) => setRForm({ ...rForm, motif: e.target.value })} data-testid="retour-motif">
              <option value="casse">Casse</option><option value="dlc">DLC dépassée</option><option value="invendu">Invendu</option><option value="erreur">Erreur de commande</option><option value="autre">Autre</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input type="checkbox" checked={rForm.recuperable} onChange={(e) => setRForm({ ...rForm, recuperable: e.target.checked })} data-testid="retour-recuperable" />
            <span className="text-sm">Produit récupérable (réintégré au stock). Sinon, enregistré comme perte.</span>
          </label>
          <p className="text-xs text-[var(--tm-muted)] mb-3">Valeur du retour : {MAD((parseFloat(rForm.prix_unitaire || 0)) * (parseFloat(rForm.quantite || 0)))} — déduite du CA Distribution après validation admin.</p>
          <button type="submit" className="tm-btn w-full mt-1" data-testid="retour-save">Enregistrer le retour</button>
        </form>
      </Modal>

      {/* Paiement modal */}
      <Modal open={pOpen} onClose={() => setPOpen(false)} title="Encaisser un paiement" testid="paiement-modal">
        {pLiv && (
          <form onSubmit={savePay}>
            <p className="text-sm text-[var(--tm-muted)] mb-3">{pLiv.client_nom} — reste à payer : <span style={{ color: "#F44336" }} className="tabular">{MAD(pLiv.reste_a_payer)}</span></p>
            <Field label="Montant reçu (DH)"><input type="number" step="0.01" className="tm-input" required value={pMontant} onChange={(e) => setPMontant(e.target.value)} data-testid="paiement-montant" /></Field>
            <button type="submit" className="tm-btn w-full mt-2" data-testid="paiement-save">Valider le paiement</button>
          </form>
        )}
      </Modal>

      {/* Retour sur une ligne de livraison (appliqué immédiatement) */}
      <Modal open={lrOpen} onClose={() => setLrOpen(false)} title="Retour produit — livraison" testid="line-retour-modal">
        {lrLiv && (
          <form onSubmit={saveLineRetour}>
            <div className="tm-card p-3 mb-4 text-sm" style={{ background: "var(--tm-bg)" }}>
              <p className="font-semibold">{lrLiv.client_nom}</p>
              <p className="text-[var(--tm-muted)] text-xs mt-1">{fmt(lrLiv.date)} · Distribué {MAD(lrLiv.total)} · Encaissé {MAD(lrLiv.montant_paye)} · Solde <span style={{ color: lrLiv.reste_a_payer > 0 ? "#F44336" : "#4CAF50" }}>{MAD(lrLiv.reste_a_payer)}</span></p>
            </div>
            <Field label="Produit retourné">
              <select className="tm-input" value={lrForm.product_nom} onChange={(e) => onLrProd(e.target.value)} data-testid="line-retour-product">
                {(lrLiv.items || []).map((i, idx) => <option key={idx} value={i.nom}>{i.nom} (livré : {i.quantite})</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantité retournée"><input type="number" step="0.01" min="0" className="tm-input" required value={lrForm.quantite} onChange={(e) => onLrQte(e.target.value)} data-testid="line-retour-qte" /></Field>
              <Field label="Montant du retour (DH)"><input type="number" step="0.01" min="0" className="tm-input" required value={lrForm.montant} onChange={(e) => setLrForm({ ...lrForm, montant: e.target.value })} data-testid="line-retour-montant" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Motif du retour">
                <select className="tm-input" value={lrForm.motif} onChange={(e) => setLrForm({ ...lrForm, motif: e.target.value })} data-testid="line-retour-motif">
                  <option value="casse">Casse</option><option value="dlc">DLC dépassée</option><option value="invendu">Invendu</option><option value="erreur">Erreur de commande</option><option value="autre">Autre</option>
                </select>
              </Field>
              <Field label="Date du retour"><input type="date" className="tm-input" value={lrForm.date} onChange={(e) => setLrForm({ ...lrForm, date: e.target.value })} data-testid="line-retour-date" /></Field>
            </div>
            <Field label="Commentaire"><textarea className="tm-input" rows={2} value={lrForm.commentaire} onChange={(e) => setLrForm({ ...lrForm, commentaire: e.target.value })} data-testid="line-retour-commentaire" /></Field>
            <p className="text-xs text-[var(--tm-muted)] mb-3">Le montant du retour ({MAD(parseFloat(lrForm.montant || 0))}) sera immédiatement déduit du montant attendu et impactera le CA net.</p>
            <button type="submit" className="tm-btn w-full mt-1" data-testid="line-retour-save">Enregistrer le retour</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
